import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useContent } from "../context/ContentContext";
import { Images, MapPin, Gauge, X, ChevronLeft, ChevronRight, Camera, Calendar } from "lucide-react";

function formatRideDate(value) {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
}

function Lightbox({ folder, onClose }) {
    const images = folder?.images || [];
    const [idx, setIdx] = useState(0);

    const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
    const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose, prev, next]);

    if (!folder) return null;
    const rideDate = formatRideDate(folder.ride_date);
    const current = images[idx] || {};

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
            data-testid="gallery-lightbox"
            onClick={onClose}
        >
            <div className="flex items-start justify-between px-6 md:px-12 py-5 border-b border-meraki-gold/15" onClick={(e) => e.stopPropagation()}>
                <div>
                    <div className="font-display text-2xl md:text-3xl uppercase text-meraki-text leading-none">{folder.ride_name}</div>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-[10px] uppercase tracking-[0.25em] text-meraki-muted">
                        {rideDate && <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3 text-meraki-gold" />{rideDate}</span>}
                        {folder.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3 text-meraki-gold" />{folder.location}</span>}
                        {folder.total_kms && <span className="inline-flex items-center gap-1.5"><Gauge className="w-3 h-3 text-meraki-gold" />{folder.total_kms}</span>}
                        <span className="text-meraki-gold">{idx + 1} / {images.length}</span>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    data-testid="gallery-lightbox-close"
                    className="p-2 border border-meraki-gold/30 text-meraki-gold hover:bg-meraki-gold hover:text-black transition-colors"
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center relative px-4 md:px-16 py-6 min-h-0" onClick={(e) => e.stopPropagation()}>
                {current.url && (
                    <img src={current.url} alt={current.caption || `${folder.ride_name} ${idx + 1}`} className="max-h-full max-w-full object-contain" />
                )}
                {current.caption && (
                    <div data-testid="gallery-lightbox-caption" className="mt-4 max-w-2xl text-center text-sm text-meraki-text/85 italic px-4">
                        {current.caption}
                    </div>
                )}
                {images.length > 1 && (
                    <>
                        <button
                            onClick={prev}
                            aria-label="Previous"
                            data-testid="gallery-lightbox-prev"
                            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-meraki-gold hover:text-black text-meraki-gold border border-meraki-gold/40 p-3 transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={next}
                            aria-label="Next"
                            data-testid="gallery-lightbox-next"
                            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-meraki-gold hover:text-black text-meraki-gold border border-meraki-gold/40 p-3 transition-colors"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </>
                )}
            </div>

            {images.length > 1 && (
                <div className="px-6 md:px-12 py-4 border-t border-meraki-gold/15 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                        {images.map((img, i) => (
                            <button
                                key={img.url || `thumb-${i}`}
                                onClick={() => setIdx(i)}
                                className={`w-16 h-16 shrink-0 overflow-hidden border transition-colors ${i === idx ? "border-meraki-gold" : "border-meraki-gold/20 opacity-60 hover:opacity-100"}`}
                            >
                                <img src={img.url} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function FolderCard({ folder, onOpen }) {
    const cover = folder.images?.[0]?.url;
    const rideDate = formatRideDate(folder.ride_date);
    return (
        <button
            data-testid={`gallery-folder-${folder.id}`}
            onClick={() => onOpen(folder)}
            className="group text-left bg-meraki-surface border border-meraki-gold/20 overflow-hidden hover:border-meraki-gold/60 transition-colors"
        >
            <div className="relative aspect-[4/3] overflow-hidden bg-black">
                {cover ? (
                    <img src={cover} alt={folder.ride_name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-meraki-gold/25">
                        <Camera className="w-16 h-16" />
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-black/75 backdrop-blur-sm border border-meraki-gold/30 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-meraki-gold">
                    <Images className="w-3 h-3" />{folder.images?.length || 0}
                </div>
                {rideDate && (
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/75 backdrop-blur-sm border border-meraki-gold/30 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-meraki-gold">
                        <Calendar className="w-3 h-3" />{rideDate}
                    </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-display text-2xl md:text-3xl uppercase text-meraki-text leading-none">{folder.ride_name}</h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[10px] uppercase tracking-[0.25em] text-meraki-muted">
                        {folder.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3 text-meraki-gold" />{folder.location}</span>}
                        {folder.total_kms && <span className="inline-flex items-center gap-1.5 text-meraki-gold"><Gauge className="w-3 h-3" />{folder.total_kms}</span>}
                    </div>
                </div>
            </div>
        </button>
    );
}

export default function Gallery() {
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const { content } = useContent();
    const g = content.gallery;

    useEffect(() => {
        api.get("/galleries").then((r) => setFolders(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);

    // Hide section entirely if there are no ride folders yet
    if (!loading && folders.length === 0) return null;

    return (
        <section id="gallery" data-testid="gallery-section" className="relative py-24 md:py-40 bg-black">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
                <div className="flex items-center gap-3 mb-6">
                    <span className="font-mono text-meraki-gold text-sm">03</span>
                    <div className="w-8 h-px bg-meraki-gold" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold">{g.eyebrow}</span>
                </div>
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                    <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.95] text-meraki-text">
                        {g.heading_line1}<br /><span className="text-meraki-gold">{g.heading_line2}</span>
                    </h2>
                    <div className="flex items-center gap-3 max-w-md">
                        <Images className="w-5 h-5 text-meraki-gold shrink-0" />
                        <p className="text-meraki-muted leading-relaxed whitespace-pre-line">{g.paragraph}</p>
                    </div>
                </div>

                {loading ? (
                    <div className="text-meraki-muted text-sm">Loading…</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {folders.map((f) => (
                            <FolderCard key={f.id} folder={f} onOpen={setActive} />
                        ))}
                    </div>
                )}
            </div>

            {active && <Lightbox folder={active} onClose={() => setActive(null)} />}
        </section>
    );
}
